import React, { useState } from "react";
import {
    Dialog,
    DialogTitle,
    DialogContent,
    DialogActions,
    Button,
    Box,
    Typography,
    Link,
    IconButton,
    CircularProgress,
} from "@mui/material";
import { toast } from "react-toastify";
import CloseIcon from "@mui/icons-material/Close";
import DownloadIcon from "@mui/icons-material/Download";
import MenuBookOutlinedIcon from "@mui/icons-material/MenuBookOutlined";
import { useTranslation } from "react-i18next";
import { url } from "../api/axios_client";

export default function UserGuideDialog({ open, onClose }) {
    const { t, i18n } = useTranslation();
    const isArabic = i18n.language === "ar";
    const [loading, setLoading] = useState(null);

    const guides = [
        {
            key: "arabic",
            label: t("UserGuide.arabic_version"),
            file: "users_guide_ar.pdf",
        },
        {
            key: "english",
            label: t("UserGuide.english_version"),
            file: "users_guide_en.pdf",
        },
    ];

    const getErrorMessage = (error, msg = "") => {
    console.error("getErrorMessage:", error);
      if (error) {
        const isArabic = i18n.language === "ar";
        return isArabic
          ? error.message_ar || error.message
          : error.message;
      }
      if (msg) return msg;
      return "An error occurred";
    };

    const handleDownload = async (file, key) => {
      try {
        setLoading(key);
        const response = await fetch(`${url}/documentation/${file}`);
        const data = await response.json();

        if (response.status === 200) {
          window.open(data.url, "_blank");
        } else {
          const errorMessage = getErrorMessage(
            data,
            "Error downloading report.",
          );
          toast.error(errorMessage);
        }
      } catch (error) {
        // Fallback: try direct download
        const errorMessage = getErrorMessage(
          error,
          "Error downloading report catch.",
        );
        toast.error(errorMessage);
      } finally {
        setLoading(null);
      }
    };

    return (
        <Dialog
            open={open}
            onClose={onClose}
            maxWidth="sm"
            fullWidth
            PaperProps={{
                sx: {
                    borderRadius: "16px",
                    direction: isArabic ? "rtl" : "ltr",
                },
            }}
        >
            <DialogTitle
                sx={{
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "space-between",
                    backgroundColor: "#339ace",
                    color: "white",
                    py: 2,
                }}
            >
                <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
                    <MenuBookOutlinedIcon />
                    <Typography variant="h6" component="span">
                        {t("UserGuide.title")}
                    </Typography>
                </Box>
                <IconButton
                    onClick={onClose}
                    sx={{
                        color: "white",
                        "&:hover": {
                            backgroundColor: "rgba(255,255,255,0.1)",
                        },
                    }}
                >
                    <CloseIcon />
                </IconButton>
            </DialogTitle>

            <DialogContent sx={{ py: 4 }}>
                <Typography
                    variant="body1"
                    sx={{ my: 3, textAlign: isArabic ? "right" : "left" }}
                >
                    {t("UserGuide.description")}
                </Typography>

                <Box
                    sx={{
                        display: "flex",
                        flexDirection: "column",
                        gap: 2,
                    }}
                >
                    {guides.map((guide) => (
                        <Box
                            key={guide.key}
                            sx={{
                                display: "flex",
                                alignItems: "center",
                                justifyContent: "space-between",
                                p: 2,
                                border: "1px solid #e0e0e0",
                                borderRadius: "12px",
                                backgroundColor: "#f9f9f9",
                                transition: "all 0.2s ease",
                                "&:hover": {
                                    backgroundColor: "#f0f7fc",
                                    borderColor: "#339ace",
                                    boxShadow: "0 2px 8px rgba(51, 154, 206, 0.15)",
                                },
                            }}
                        >
                            <Typography
                                variant="body1"
                                sx={{ fontWeight: 500, color: "#333" }}
                            >
                                {guide.label}
                            </Typography>
                            <Link
                                component="button"
                                onClick={() => handleDownload(guide.file, guide.key)}
                                disabled={loading === guide.key}
                                sx={{
                                    display: "flex",
                                    alignItems: "center",
                                    gap: 0.5,
                                    color: loading === guide.key ? "#999" : "#339ace",
                                    textDecoration: "none",
                                    fontWeight: 500,
                                    cursor: loading === guide.key ? "wait" : "pointer",
                                    "&:hover": {
                                        textDecoration: loading === guide.key ? "none" : "underline",
                                    },
                                }}
                            >
                                {loading === guide.key ? (
                                    <CircularProgress size={16} sx={{ color: "#339ace" }} />
                                ) : (
                                    <DownloadIcon fontSize="small" />
                                )}
                                {loading === guide.key ? t("UserGuide.loading") : t("UserGuide.download")}
                            </Link>
                        </Box>
                    ))}
                </Box>
            </DialogContent>

            <DialogActions sx={{ px: 3, pb: 3 }}>
                <Button
                    onClick={onClose}
                    variant="outlined"
                    sx={{
                        borderRadius: "10px",
                        borderColor: "#339ace",
                        color: "#339ace",
                        "&:hover": {
                            borderColor: "#2a7ba8",
                            backgroundColor: "rgba(51, 154, 206, 0.05)",
                        },
                    }}
                >
                    {t("UserGuide.close")}
                </Button>
            </DialogActions>
        </Dialog>
    );
}
